import React, { useEffect, useRef, useState } from 'react'
import { createSimpleStore } from 'react-simple-reducer'
import { useHistory } from 'react-router-dom'
import {
  Button,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Checkbox,
  Table,
  Pagination,
  Tag,
  notification,
  ConfigProvider,
} from 'antd'
import { PlusOutlined, CloseOutlined } from '@ant-design/icons'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import ptBR from 'antd/es/date-picker/locale/pt_BR'
import ptBRLocale from 'antd/es/locale/pt_BR'
import { Popconfirm } from 'antd'
import {
  listParceiros,
  listCursosAtivos,
  listParceirosPais,
  setParceiroAtivo,
  CursoParaPreco,
  ParceiroPaiOption,
} from './parceirosApi'

const CATEGORIA_ESPECIALIZACAO = 'Curso de Especialização'

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'] // prettier-ignore

const UF_COLORS: Record<string, string> = {
  // Norte
  AC: 'green',
  AM: 'green',
  AP: 'green',
  PA: 'green',
  RO: 'green',
  RR: 'green',
  TO: 'green',
  // Nordeste
  AL: 'gold',
  BA: 'gold',
  CE: 'gold',
  MA: 'gold',
  PB: 'gold',
  PE: 'gold',
  PI: 'gold',
  RN: 'gold',
  SE: 'gold',
  // Centro-Oeste
  DF: 'purple',
  GO: 'purple',
  MS: 'purple',
  MT: 'purple',
  // Sudeste
  ES: 'blue',
  MG: 'blue',
  RJ: 'blue',
  SP: 'blue',
  // Sul
  PR: 'cyan',
  RS: 'cyan',
  SC: 'cyan',
}

interface ParceiroRow {
  id: number
  nome: string
  cpfCnpj: string
  uf: string
  parceiroPaiNome: string | null
  inativadoEm: string | null
  matriculasCount: number
  ultimaMatricula: string | null
  createdAt: string
  precoConfigurado: number | null
}

const INITIAL_FILTERS = {
  nome: '',
  uf: '',
  createdAtFrom: '',
  createdAtTo: '',
  matriculasMin: undefined as number | undefined,
  matriculasMax: undefined as number | undefined,
  cursoId: undefined as number | undefined,
  parceiroPaiId: undefined as number | undefined,
  somenteForaDoPadrao: false,
  incluirInativos: false,
}

const INITIAL_SORT = { sortBy: 'createdAt' as string, sortDir: 'desc' as 'asc' | 'desc' }

const ParceirosListStore = createSimpleStore(
  {
    rows: [] as ParceiroRow[],
    total: 0,
    loading: false,
    page: 1,
    pageSize: 20,
    filters: INITIAL_FILTERS,
    sort: INITIAL_SORT,
  },
  {
    fetchStarted(state) {
      state.loading = true
    },
    fetchSuccess(state, payload: { rows: ParceiroRow[]; count: number }) {
      state.rows = payload.rows
      state.total = payload.count
      state.loading = false
    },
    fetchError(state) {
      state.loading = false
    },
    setFilter(state, filters: Partial<typeof INITIAL_FILTERS>) {
      state.filters = { ...state.filters, ...filters }
      state.page = 1
    },
    setSort(state, sort: typeof INITIAL_SORT) {
      state.sort = sort
      state.page = 1
    },
    setPagination(state, payload: { page: number; pageSize: number }) {
      state.page = payload.page
      state.pageSize = payload.pageSize
    },
    resetFilters(state) {
      state.filters = INITIAL_FILTERS
      state.sort = INITIAL_SORT
      state.page = 1
    },
  },
  {
    thunks: {
      fetch(filterOverrides: Partial<typeof INITIAL_FILTERS> = {}) {
        return async (dispatch, getState) => {
          const { filters, sort, page, pageSize } = getState()
          const effectiveFilters = { ...filters, ...filterOverrides }
          if (Object.keys(filterOverrides).length > 0) {
            dispatch(ParceirosListStore.actions.setFilter(filterOverrides))
          }
          dispatch(ParceirosListStore.actions.fetchStarted())
          try {
            const params: any = { page, pageSize }
            if (effectiveFilters.nome) params.nome = effectiveFilters.nome
            if (effectiveFilters.uf) params.uf = effectiveFilters.uf
            if (effectiveFilters.createdAtFrom)
              params.createdAtFrom = effectiveFilters.createdAtFrom
            if (effectiveFilters.createdAtTo) params.createdAtTo = effectiveFilters.createdAtTo
            if (effectiveFilters.matriculasMin !== undefined)
              params.matriculasMin = effectiveFilters.matriculasMin
            if (effectiveFilters.matriculasMax !== undefined)
              params.matriculasMax = effectiveFilters.matriculasMax
            if (effectiveFilters.cursoId !== undefined) params.cursoId = effectiveFilters.cursoId
            if (effectiveFilters.parceiroPaiId !== undefined)
              params.parceiroPaiId = effectiveFilters.parceiroPaiId
            if (effectiveFilters.somenteForaDoPadrao) params.somenteForaDoPadrao = true
            if (effectiveFilters.incluirInativos) params.incluirInativos = true
            if (sort.sortBy) {
              params.sortBy = sort.sortBy
              params.sortDir = sort.sortDir
            }
            const result = await listParceiros(params)
            dispatch(ParceirosListStore.actions.fetchSuccess(result))
          } catch (err: any) {
            notification.error({
              message: err?.response?.data?.message ?? 'Erro ao buscar parceiros',
            })
            dispatch(ParceirosListStore.actions.fetchError())
          }
        }
      },
    },
  }
)

export function ParceirosList() {
  return (
    <ParceirosListStore.Provider init={(dispatch) => dispatch(ParceirosListStore.thunks.fetch())}>
      <ConfigProvider locale={ptBRLocale}>
        <PageLayout>
          <ParceirosListHeader />
          <ParceirosListFilters />
          <ParceirosListTable />
          <ParceirosListPagination />
        </PageLayout>
      </ConfigProvider>
    </ParceirosListStore.Provider>
  )
}

function ParceirosListHeader() {
  const history = useHistory()
  return (
    <HeaderRow>
      <PageTitle>Parceiros</PageTitle>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => history.push('/parceiros/novo')}
      >
        Novo Parceiro
      </Button>
    </HeaderRow>
  )
}

function ParceirosListFilters() {
  const { filters } = ParceirosListStore.useState()
  const dispatch = ParceirosListStore.useDispatch()
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const [nomeInputValue, setNomeInputValue] = useState(filters.nome)
  const [cursos, setCursos] = useState<CursoParaPreco[]>([])
  const [parceirosPais, setParceirosPais] = useState<ParceiroPaiOption[]>([])

  // Load the offered courses once and default the price column to the first "especialização".
  useEffect(() => {
    listCursosAtivos()
      .then((ativos) => {
        setCursos(ativos)
        const padrao =
          ativos.find((curso) => curso.categorias === CATEGORIA_ESPECIALIZACAO) ?? ativos[0]
        if (padrao) dispatch(ParceirosListStore.thunks.fetch({ cursoId: padrao.id }))
      })
      .catch(() => notification.error({ message: 'Erro ao carregar cursos' }))
  }, [dispatch])

  useEffect(() => {
    listParceirosPais()
      .then(setParceirosPais)
      .catch(() => notification.error({ message: 'Erro ao carregar parceiros pais' }))
  }, [])

  function handleParceiroPaiChange(parceiroPaiId: number | undefined) {
    dispatch(ParceirosListStore.thunks.fetch({ parceiroPaiId }))
  }

  function handleCursoChange(cursoId: number) {
    dispatch(ParceirosListStore.thunks.fetch({ cursoId }))
  }

  function handleForaDoPadraoChange(checked: boolean) {
    dispatch(ParceirosListStore.thunks.fetch({ somenteForaDoPadrao: checked }))
  }

  function handleIncluirInativosChange(checked: boolean) {
    dispatch(ParceirosListStore.thunks.fetch({ incluirInativos: checked }))
  }

  function handleNomeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const nome = e.target.value
    setNomeInputValue(nome)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      dispatch(ParceirosListStore.thunks.fetch({ nome }))
    }, 500)
  }

  function handleUfChange(value: string) {
    dispatch(ParceirosListStore.thunks.fetch({ uf: value ?? '' }))
  }

  function handleDateRangeChange(dates: any) {
    dispatch(
      ParceirosListStore.thunks.fetch({
        createdAtFrom: dates?.[0] ? dates[0].toISOString() : '',
        createdAtTo: dates?.[1] ? dates[1].toISOString() : '',
      })
    )
  }

  function handleMatriculasMinChange(value: number | null) {
    dispatch(ParceirosListStore.thunks.fetch({ matriculasMin: value ?? undefined }))
  }

  function handleMatriculasMaxChange(value: number | null) {
    dispatch(ParceirosListStore.thunks.fetch({ matriculasMax: value ?? undefined }))
  }

  function handleClearFilters() {
    clearTimeout(debounceRef.current)
    setNomeInputValue('')
    dispatch(ParceirosListStore.actions.resetFilters())
    dispatch(ParceirosListStore.thunks.fetch(INITIAL_FILTERS))
  }

  return (
    <FiltersContainer>
      <FiltersHeader>
        <FiltersTitle>Filtros</FiltersTitle>
        <Button type="text" size="small" onClick={handleClearFilters} icon={<CloseOutlined />}>
          Limpar filtros
        </Button>
      </FiltersHeader>
      <FiltersRow>
        <Input
          placeholder="Buscar por nome"
          value={nomeInputValue}
          onChange={handleNomeChange}
          style={{ width: 220 }}
          allowClear
        />
        <Select
          placeholder="UF"
          value={filters.uf || undefined}
          onChange={handleUfChange}
          allowClear
          showSearch
          optionFilterProp="value"
          style={{ width: 100 }}
          options={UFS.map((uf) => ({ value: uf, label: uf }))}
        />
        <Select
          placeholder="Parceiro pai"
          value={filters.parceiroPaiId}
          onChange={handleParceiroPaiChange}
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 220 }}
          options={parceirosPais.map((pai) => ({ value: pai.id, label: pai.nome }))}
        />
        <DatePicker.RangePicker
          locale={ptBR}
          format="DD/MM/YYYY"
          placeholder={['Criação desde', 'Criação até']}
          onChange={handleDateRangeChange}
          value={[
            filters.createdAtFrom ? dayjs(filters.createdAtFrom) : null,
            filters.createdAtTo ? dayjs(filters.createdAtTo) : null,
          ]}
        />
        <InputNumber
          placeholder="Matrículas mínimo"
          value={filters.matriculasMin}
          onChange={handleMatriculasMinChange}
          min={0}
          style={{ width: 160 }}
        />
        <InputNumber
          placeholder="Matrículas máximo"
          value={filters.matriculasMax}
          onChange={handleMatriculasMaxChange}
          min={0}
          style={{ width: 160 }}
        />
        <Checkbox
          checked={filters.incluirInativos}
          onChange={(e) => handleIncluirInativosChange(e.target.checked)}
        >
          Incluir inativos
        </Checkbox>
      </FiltersRow>
      <PrecoSegment>
        <SegmentLabel>Preço</SegmentLabel>
        <Checkbox
          checked={filters.somenteForaDoPadrao}
          onChange={(e) => handleForaDoPadraoChange(e.target.checked)}
        >
          Somente parceiros com preço fora do padrão
        </Checkbox>
        <Select
          placeholder="Curso (preço)"
          value={filters.cursoId}
          onChange={handleCursoChange}
          showSearch
          optionFilterProp="label"
          style={{ width: 220, marginLeft: 'auto' }}
          options={cursos.map((curso) => ({ value: curso.id, label: curso.nomeReduzido }))}
        />
      </PrecoSegment>
    </FiltersContainer>
  )
}

function ParceirosListTable() {
  const { rows, loading, sort } = ParceirosListStore.useState()
  const dispatch = ParceirosListStore.useDispatch()
  const history = useHistory()

  function handleSortChange(column: string) {
    const newDir = sort.sortBy === column && sort.sortDir === 'asc' ? 'desc' : 'asc'
    const newSort = { sortBy: column, sortDir: newDir as 'asc' | 'desc' }
    dispatch(ParceirosListStore.actions.setSort(newSort))
    dispatch(ParceirosListStore.thunks.fetch())
  }

  async function handleToggleAtivo(row: ParceiroRow) {
    try {
      await setParceiroAtivo(row.id, row.inativadoEm != null) // if inactive, reactivate
      dispatch(ParceirosListStore.thunks.fetch())
    } catch (err: any) {
      notification.error({ message: err?.response?.data?.message ?? 'Erro ao atualizar parceiro' })
    }
  }

  function sortIndicator(column: string) {
    if (sort.sortBy !== column) return ''
    return sort.sortDir === 'asc' ? ' ↑' : ' ↓'
  }

  const columns = [
    {
      title: (
        <SortableHeader onClick={() => handleSortChange('nome')}>
          Nome{sortIndicator('nome')}
        </SortableHeader>
      ),
      dataIndex: 'nome',
      render: (_: any, row: ParceiroRow) => (
        <span>
          <a onClick={() => history.push(`/parceiros/${row.id}`)}>{row.nome}</a>
          {row.inativadoEm != null && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              Inativo
            </Tag>
          )}
        </span>
      ),
    },
    { title: 'CPF/CNPJ', dataIndex: 'cpfCnpj' },
    {
      title: 'Parceiro pai',
      dataIndex: 'parceiroPaiNome',
      render: (nome: string | null) =>
        nome ? <Tag color="orange">{nome}</Tag> : <span style={{ color: '#bbb' }}>—</span>,
    },
    {
      title: 'UF',
      dataIndex: 'uf',
      render: (uf: string) => (uf ? <Tag color={UF_COLORS[uf] ?? 'default'}>{uf}</Tag> : '—'),
    },
    {
      title: (
        <SortableHeader onClick={() => handleSortChange('matriculas')}>
          Matrículas{sortIndicator('matriculas')}
        </SortableHeader>
      ),
      dataIndex: 'matriculasCount',
    },
    {
      title: 'Preço configurado',
      dataIndex: 'precoConfigurado',
      align: 'right' as const,
      render: (valor: number | null) => (valor == null ? '—' : formatBRL(valor)),
    },
    {
      title: (
        <SortableHeader onClick={() => handleSortChange('ultimaMatricula')}>
          Última Matrícula{sortIndicator('ultimaMatricula')}
        </SortableHeader>
      ),
      dataIndex: 'ultimaMatricula',
      render: (value: string | null) => (value ? dayjs(value).format('DD/MM/YYYY') : '—'),
    },
    {
      title: (
        <SortableHeader onClick={() => handleSortChange('createdAt')}>
          Data de Criação{sortIndicator('createdAt')}
        </SortableHeader>
      ),
      dataIndex: 'createdAt',
      render: (value: string) => dayjs(value).format('DD/MM/YYYY'),
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_: any, row: ParceiroRow) => (
        <Popconfirm
          title={row.inativadoEm != null ? 'Reativar parceiro' : 'Inativar parceiro'}
          description={
            row.inativadoEm != null
              ? 'O parceiro voltará a aparecer normalmente.'
              : 'O parceiro deixará de aparecer na listagem padrão.'
          }
          okText={row.inativadoEm != null ? 'Reativar' : 'Inativar'}
          cancelText="Cancelar"
          okButtonProps={{ danger: row.inativadoEm == null }}
          onConfirm={() => handleToggleAtivo(row)}
        >
          <Button size="small" danger={row.inativadoEm == null}>
            {row.inativadoEm != null ? 'Reativar' : 'Inativar'}
          </Button>
        </Popconfirm>
      ),
    },
  ]

  return (
    <TableContainer>
      <Table dataSource={rows} columns={columns} rowKey="id" loading={loading} pagination={false} />
    </TableContainer>
  )
}

function ParceirosListPagination() {
  const { total, page, pageSize } = ParceirosListStore.useState()
  const dispatch = ParceirosListStore.useDispatch()

  // rc-pagination fires onChange for a page-size change too, so this covers both.
  function handlePageChange(newPage: number, newPageSize: number) {
    dispatch(ParceirosListStore.actions.setPagination({ page: newPage, pageSize: newPageSize }))
    dispatch(ParceirosListStore.thunks.fetch())
  }

  return (
    <PaginationRow>
      <Pagination
        current={page}
        pageSize={pageSize}
        total={total}
        showSizeChanger
        showTotal={(quantidade, faixa) => `${faixa[0]}-${faixa[1]} de ${quantidade} parceiros`}
        onChange={handlePageChange}
      />
    </PaginationRow>
  )
}

function formatBRL(valor: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)
}

const PageLayout = styled.div`
  padding: 20px;
  background: whitesmoke;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PageTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const FiltersContainer = styled.div`
  padding: 16px 20px;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
`

const FiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const FiltersTitle = styled.span`
  font-size: 16px;
  color: #494d50;
  font-weight: 600;
`

const FiltersRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`

const PrecoSegment = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`

const SegmentLabel = styled.span`
  font-size: 12px;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`

const TableContainer = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 8px;
`

const PaginationRow = styled.div`
  display: flex;
  justify-content: flex-end;
`

const SortableHeader = styled.span`
  cursor: pointer;
  user-select: none;
  &:hover {
    color: #1890ff;
  }
`
